import { Box, Theme, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '../contexts/themeContext';
import { Match } from '../firebase/api';

const styles = {
  wrapper: {
    width: '100%',
    alignItems: 'stretch',
    marginTop: 2,
  },
  scrollerWrapper: {
    position: 'relative',
  },
  scroller: {
    overflowX: 'auto',
    maxWidth: '100%',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  labelRow: {
    display: 'flex',
    gap: 0.75,
    justifyContent: 'start',
    paddingBottom: 1,
  },
  row: {
    display: 'flex',
    gap: 0.75,
    justifyContent: 'start',
    paddingBottom: 2,
  },
  square: {
    width: 24,
    height: 24,
    borderRadius: 1,
    flex: '0 0 auto',
  },
  labelMonth: {
    fontSize: 10,
    color: 'text.secondary',
    textTransform: 'lowercase',
    width: 24,
    flex: '0 0 auto',
    lineHeight: 1,
    textAlign: 'center',
  },
  fade: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 18,
    pointerEvents: 'none',
  },
};

const startOfMonth = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), 1);

const addMonths = (date: Date, months: number) =>
  new Date(date.getFullYear(), date.getMonth() + months, 1);

const toMonthKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

export interface ActivityChartProps {
  matches?: Match[];
  userId: string;
}

export function ActivityChart({ matches, userId }: ActivityChartProps) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [fadeLeft, setFadeLeft] = useState(false);
  const [fadeRight, setFadeRight] = useState(false);
  const { darkModeEnabled } = useTheme();
  const updateFades = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const { scrollLeft, clientWidth, scrollWidth } = scroller;
    setFadeLeft(scrollLeft > 1);
    setFadeRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  const squares = useMemo(() => {
    const today = startOfMonth(new Date());
    const counts = new Map<string, number>();
    let earliest: Date | null = null;

    for (const match of matches ?? []) {
      if (match.winner.id !== userId && match.loser.id !== userId) continue;

      const matchDate = startOfMonth(new Date(match.createdAt));
      const key = toMonthKey(matchDate);
      counts.set(key, (counts.get(key) ?? 0) + 1);

      if (earliest === null || matchDate < earliest) {
        earliest = matchDate;
      }
    }

    const startDate = earliest ?? today;
    const monthCount =
      (today.getFullYear() - startDate.getFullYear()) * 12 +
      (today.getMonth() - startDate.getMonth()) +
      1;

    return Array.from({ length: monthCount }, (_, index) => {
      const date = addMonths(startDate, index);
      const count = counts.get(toMonthKey(date)) ?? 0;

      return { date, count };
    });
  }, [matches, userId]);

  const labels = useMemo(
    () =>
      squares.map(({ date }, index) => {
        const month = date.getMonth();
        const isCurrentMonth = index === squares.length - 1;

        if (
          isCurrentMonth ||
          month === 0 ||
          month === 3 ||
          month === 6 ||
          month === 9
        )
          return {
            0: String(date.getFullYear()),
            1: 'feb',
            2: 'mar',
            3: 'apr',
            4: 'maj',
            5: 'jun',
            6: 'jul',
            7: 'aug',
            8: 'sep',
            9: 'okt',
            10: 'nov',
            11: 'dec',
          }[month];

        return '';
      }),
    [squares],
  );

  const getColorForCount = (count: number, theme: Theme) => {
    if (darkModeEnabled) {
      if (count >= 5) return theme.palette.success.main;
      if (count > 0) return theme.palette.success.dark;
      return '#ffffff1f';
    }

    if (count >= 5) return theme.palette.success.main;
    if (count > 0) return theme.palette.success.light;
    return '#0000001f';
  };

  useEffect(() => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollLeft = scrollerRef.current.scrollWidth;
    requestAnimationFrame(updateFades);
  }, [squares.length, updateFades]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    updateFades();
    const handleScroll = () => updateFades();
    scroller.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updateFades);

    return () => {
      scroller.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateFades);
    };
  }, [squares.length, updateFades]);

  return (
    <Box sx={styles.wrapper}>
      <Box sx={styles.scrollerWrapper}>
        {fadeLeft && (
          <Box
            sx={{
              ...styles.fade,
              left: 0,
              background: (theme: Theme) =>
                `linear-gradient(to right, ${theme.palette.background.default}, rgba(0, 0, 0, 0))`,
            }}
          />
        )}
        {fadeRight && (
          <Box
            sx={{
              ...styles.fade,
              right: 0,
              background: (theme: Theme) =>
                `linear-gradient(to left, ${theme.palette.background.default}, rgba(0, 0, 0, 0))`,
            }}
          />
        )}
        <Box sx={styles.scroller} ref={scrollerRef}>
          <Box sx={styles.labelRow}>
            {labels.map((label, index) => (
              <Typography key={`label-${index}`} sx={styles.labelMonth}>
                {label}
              </Typography>
            ))}
          </Box>
          <Box sx={styles.row}>
            {squares.map(({ date, count }) => (
              <Tooltip
                key={toMonthKey(date)}
                title={`${count} match${count !== 1 ? 'er' : ''} under ${date.toLocaleDateString(
                  'sv-SE',
                  { year: 'numeric', month: 'long' },
                )}`}
                placement="top"
                enterTouchDelay={300}
              >
                <Box
                  sx={{
                    ...styles.square,
                    backgroundColor: (theme: Theme) =>
                      getColorForCount(count, theme),
                  }}
                />
              </Tooltip>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
